import { apiClient } from "~/api/client";
import type { DealClientListData, DealData } from "~/types/deal";

// ============================================================================
// Core Deal Types
// ============================================================================

export interface LockCost {
  coins: number;
}

export interface PriceDetail {
  coins: number;
  price: number;
  shipping: number;
  shippingAsCoins: number;
}

export interface PriceProperty {
  lockCost?: LockCost;
  restricted?: boolean;
  price?: Record<string, PriceDetail>;
}

export interface PriceLevelSet {
  solo?: PriceProperty;
  target?: PriceProperty;
  join?: PriceProperty;
  retail?: PriceProperty;
  sale?: PriceProperty;
  min?: PriceProperty;
  max?: PriceProperty;
}

export interface DealAttributes {
  name?: string;
  optionCount?: number;
}

// ============================================================================
// Request/Response Types
// ============================================================================

export interface DealSuggestionRequest {
  q: string;
  channel?: string;
}

export interface DealSuggestionResponse {
  deals: DealClientListData[];
}

export interface GoogleCategoryTreeRequest {
  parentId?: string;
  entryPoint?: string;
  count?: number;
}

export interface GoogleCategoryTree {
  categoryId: string;
  parentId?: string;
  category?: string;
  fullCategories?: string;
  imageUrl?: string;
}

export interface GoogleCategoryTreeResponse {
  categories: GoogleCategoryTree[];
}

export interface GoogleCategoryTreeInDashboardRequest {
  count?: number;
}

export interface DealsInGoogleCategoryTreeRequest {
  categoryId: string;
  page?: number;
  size?: number;
  entryPoint?: string;
  channel?: string;
}

export interface DealsInGoogleCategoryTreeResponse {
  deals: DealClientListData[];
  total?: number;
}

export interface BrandDealRequest {
  brandName: string;
  page?: number;
  size?: number;
  channel?: string;
}

export interface BrandDealResponse {
  deals: DealClientListData[];
}

export interface CategoryWithSectionsRequest {
  entryPoint: string;
  categoryCount?: number;
  dealCountPerCategory?: number;
  channel?: string;
}

export interface DtoCategoryWithDeals {
  category?: GoogleCategoryTree;
  deals?: DealClientListData[];
}

export interface CategoryWithSectionsResponse {
  categoriesWithDeals?: DtoCategoryWithDeals[];
}

export interface BestsellerDealsRequest {
  entryPoint: string;
  dealCount?: number;
  channel?: string;
}

export interface BestsellerDealsResponse {
  deals: DealClientListData[];
  total?: number;
}

export type EntryPoint =
  | "popular"
  | "low_value"
  | "branded"
  | "deal_race"
  | "flash_sale"
  | "dealFreeze"
  | "high_discount"
  | "best_seller"
  | "hot_sale"
  | "fast_delivery"
  | "gc_grocery"
  | "watches"
  | "gadgets"
  | "shoes"
  | "fashion"
  | "accessories"
  | "walletsBags"
  | "automotive"
  | "homeDecor"
  | "hobbies"
  | "phoneUpgrades"
  | "sportsOutdoors"
  | "kids"
  | "health"
  | "diy"
  | "adult"
  | "google_category"
  | "category_recommend"
  | "subsidy_deals"
  | "super_deals"
  | "free_deals"
  | "budget_deals";

export interface DealFeedRequest {
  entryPoint: EntryPoint | string;
  page?: number;
  pageSize?: number;
  channel?: string;
}

export interface DealFeedResponse {
  deals: DealClientListData[];
  total?: number;
}

export interface SearchDealRequest {
  q: string;
  page?: number;
  pageSize?: number;
  channel?: string;
}

export interface SimilarDealsRequest {
  dealId: string;
  page?: number;
  pageSize?: number;
  channel?: string;
}

export type DtoBuySource = "internal" | "external" | "ad_link";

export interface DealDataRequest {
  dealId: string;
  buySource?: DtoBuySource;
  channel?: string;
  reqId?: string;
}

// Legacy interfaces for backwards compatibility
export interface DtoPriceBreakdownForDuoDeal {
  itemPrice?: number;
  shippingFee?: number;
  processingFee?: number;
  discount?: number;
  fincoinDiscountAmount?: number;
  fincoinDiscountMonetaryValue?: number;
  finalPrice?: number;
  strikethroughPrice?: number;
}

export interface DtoPriceBreakdownForFlashSale {
  fincoinAmount?: number;
  fincoinMonetaryValue?: number;
  shippingFee?: number;
  strikethroughPrice?: number;
  finalPrice?: number;
}

export interface DtoPriceBreakdownForTreasureHunt {
  strikethroughPrice?: number;
}

export interface DtoPricesOfBuyingTypes {
  teamBuy?: DtoPriceBreakdownForDuoDeal;
  join?: DtoPriceBreakdownForDuoDeal;
  buyAlone?: DtoPriceBreakdownForDuoDeal;
  flashSale?: DtoPriceBreakdownForFlashSale;
  treasureHunt?: DtoPriceBreakdownForTreasureHunt;
}

export interface DealDataResponse {
  deal?: DealData;
  priceBreakdown?: Record<string, DtoPricesOfBuyingTypes>;
}

export interface DuoDealEntryDataRequest {
  duoDealEntryId: string;
  channel?: string;
}

export interface DuoDealEntryDataResponse {
  duoDealEntry?: Record<string, unknown>;
  deal?: Record<string, unknown>;
  priceBreakdown?: Record<string, DtoPricesOfBuyingTypes>;
}

export interface ProductLiveDealsRequest {
  productId: string;
}

export interface ProductLiveDealsResponse {
  result: {
    count: number;
    deals: DealClientListData[];
  };
}

export interface EmptyRequest {}

export interface SubsidyCategoriesResp {
  categories: GoogleCategoryTree[];
}

export interface DashboardSubsidyDealsRequest {
  googleCategoryLv1Id: string;
  page?: number;
  size?: number;
}

export interface DashboardSubsidyDealsResponse {
  deals: DealClientListData[];
  total?: number;
}

export interface SearchSuggestionRequest {
  q: string;
}

export interface SearchSuggestionResponse {
  suggestions?: string[];
}

// Converter function to transform DealData to DealClientListData
export function convertDealDataToDealClientListData(dealData: DealData, userCurrency = "USD"): DealClientListData {
  const product = dealData.product;

  // Get the first SKU for pricing information
  const firstSku = product.skus[0];
  const basePrice = firstSku?.prices?.[userCurrency] || firstSku?.prices?.USD || product.productCost || 0;
  const retailPrice = product.retailPrice?.[userCurrency] || product.retailPrice?.USD || basePrice;

  return {
    _id: dealData._id,
    state: dealData.state,
    _productId: product._id,
    productName: product.name,
    type: dealData.type,
    image: product.images[0] || undefined,
    rating: product.rating || undefined,
    ratingCount: undefined, // Not available in deal_data response
    price: basePrice,
    strikethroughPrice: retailPrice > basePrice ? retailPrice : undefined,
    fincoinPrice: firstSku?.coins || undefined,
    duoDeal: dealData.duoDeal,
    gamified: dealData.gamified,
    dealRace: dealData.dealRace,
    soldCount: product.soldCount || undefined,
    tags: dealData.tags,
    hasAdultContent: product.hasAdultContent,
    imageKitEnabled: dealData.imageKitEnabled,
    lastUpdated: dealData.lastUpdated,
    endTime: dealData.endTime || undefined,
    productCost: product.productCost,
    branded: product.brandedItem,
    // Optional fields that may not be present
    priceLevels: undefined,
    lowestPriceInUsdCent: undefined,
    reallocatedPrice: undefined,
    reviews: undefined,
    retailPrice: undefined,
    quantity: undefined,
    minLockCost: dealData.minLockCost || undefined,
    priceCurve: undefined,
    coinCostCurve: undefined,
    duoDealEntries: undefined,
    timerStart: undefined,
    timerEnd: undefined,
    recentRacers: undefined,
    amountOfRacers: undefined,
    usersWon: undefined,
    maxRacer: undefined,
    leaderboardActivated: undefined,
    productLikes: product.likes || undefined,
    supplierName: undefined,
    supplierStoreId: undefined,
    fromProducer: undefined,
    reqId: dealData.reqId,
    attributes: undefined,
    data: undefined,
    startTime: undefined,
  };
}

// ============================================================================
// API Functions
// ============================================================================

export const dealApi = {
  // Get deal suggestions for a video title
  async getDealSuggestions(request: DealSuggestionRequest): Promise<DealSuggestionResponse> {
    return apiClient.makeAuthenticatedRequest<DealSuggestionResponse>("/deal/deal_suggestions", { params: request });
  },

  // Get Google categories tree
  async getGoogleCategories(request: GoogleCategoryTreeRequest): Promise<GoogleCategoryTreeResponse> {
    return apiClient.makeAuthenticatedRequest<GoogleCategoryTreeResponse>("/deal/google_categories", {
      params: request,
    });
  },

  // Get recommended categories for dashboard
  async getGoogleCategoriesInDashboard(
    request: GoogleCategoryTreeInDashboardRequest
  ): Promise<GoogleCategoryTreeResponse> {
    return apiClient.makeAuthenticatedRequest<GoogleCategoryTreeResponse>("/deal/google_categories_in_dashboard", {
      params: request,
    });
  },

  // Get deals in Google category tree
  async getDealsInGoogleCategoryTree(
    request: DealsInGoogleCategoryTreeRequest
  ): Promise<DealsInGoogleCategoryTreeResponse> {
    return apiClient.makeAuthenticatedRequest<DealsInGoogleCategoryTreeResponse>("/deal/google_categories/list", {
      params: request,
    });
  },

  // Get deals for a brand
  async getBrandDeals(request: BrandDealRequest): Promise<BrandDealResponse> {
    return apiClient.makeAuthenticatedRequest<BrandDealResponse>("/deal/brands/deals", { params: request });
  },

  // Get categories with sections
  async getCategoryWithSections(request: CategoryWithSectionsRequest): Promise<CategoryWithSectionsResponse> {
    return apiClient.makeAuthenticatedRequest<CategoryWithSectionsResponse>("/deal/category_with_sections", {
      params: request,
    });
  },

  // Get bestseller deals
  async getBestsellerDeals(request: BestsellerDealsRequest): Promise<BestsellerDealsResponse> {
    return apiClient.makeAuthenticatedRequest<BestsellerDealsResponse>("/deal/bestseller_deals", { params: request });
  },

  // Get deals in specified feed
  async getDealFeed(request: DealFeedRequest): Promise<DealFeedResponse> {
    return apiClient.makeAuthenticatedRequest<DealFeedResponse>("/deal/deal_feed", { params: request });
  },

  // Search deals
  async searchDeals(request: SearchDealRequest): Promise<DealFeedResponse> {
    return apiClient.makeAuthenticatedRequest<DealFeedResponse>("/deal/search_deals", { params: request });
  },

  // Get similar deals
  async getSimilarDeals(request: SimilarDealsRequest): Promise<DealFeedResponse> {
    return apiClient.makeAuthenticatedRequest<DealFeedResponse>("/deal/similar_deals", { params: request });
  },

  // Get deal data
  async getDealData(request: DealDataRequest): Promise<DealDataResponse> {
    const response = await apiClient.makeAuthenticatedRequest<DealDataResponse>("/deal/deal_data", {
      params: request,
    });
    return {
      deal: response.deal,
      priceBreakdown: response.priceBreakdown,
    };
  },

  // Get duo deal entry data
  async getDuoDealEntryData(request: DuoDealEntryDataRequest): Promise<DuoDealEntryDataResponse> {
    return apiClient.makeAuthenticatedRequest<DuoDealEntryDataResponse>("/deal/duo_deal_entry_data", {
      params: request,
    });
  },

  // Get subsidy categories
  async getSubsidyCategories(request: EmptyRequest = {}): Promise<SubsidyCategoriesResp> {
    return apiClient.makeAuthenticatedRequest<SubsidyCategoriesResp>("/deal/subsidy_categories", { params: request });
  },

  // Get product live deals
  async getProductLiveDeals(request: ProductLiveDealsRequest): Promise<ProductLiveDealsResponse> {
    const { productId, ...params } = request;
    return apiClient.makeAuthenticatedRequest<ProductLiveDealsResponse>(`/product/${productId}/live_deals`, { params });
  },

  // Get subsidy deals from dashboard by category level 1 id
  async getDashboardSubsidyDeals(request: DashboardSubsidyDealsRequest): Promise<DashboardSubsidyDealsResponse> {
    return apiClient.makeAuthenticatedRequest<DashboardSubsidyDealsResponse>("/dashboard/subsidy_deals", {
      params: request,
    });
  },

  // Search Suggestion
  async getSearchSuggestion(request: SearchSuggestionRequest): Promise<SearchSuggestionResponse> {
    return apiClient.makeAuthenticatedRequest<SearchSuggestionResponse>("/deal/search_suggestion", {
      params: request,
    });
  },
};
